import { Stack } from '@mui/system';

import { CSSProperties, FC, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { jwtDecode } from 'jwt-decode';

import { refreshTokenAPI } from 'common/api';
import ModalEle from 'common/components/Modal/ModalEle';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { BAD_REQUEST_MSG, USER_NOT_FOUND } from 'common/constants/index';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import storageWrapper from 'common/loginUtils/storage-wrapper';
import { RootState } from 'common/store/AppStore';
import {
	setErrorMessage,
	setIsAuthenticated,
	setIsError,
	setLoggedInUser,
} from 'common/store/reducer/commonReducer';
import { onLogout, storageCleanAndRedirect } from 'common/utils/authCheck';

import { getUserInfo } from 'features/Settings/UserManagement/api';

interface AuthProviderProps {
	children: React.ReactNode;
}

const { OK, BAD_REQUEST, NOT_FOUND, FORBIDDEN } = HTTP_STATUS_CODES;

const AuthProvider: FC<AuthProviderProps> = ({
	children,
}: AuthProviderProps) => {
	const dispatch = useDispatch();
	const [refreshing, setRefreshing] = useState<boolean>(false);
	const deleteAIGeneratedCardPayload = useSelector(
		(state: RootState) => state.common.deleteAIGeneratedCardPayload,
	);
	const isRegenerateMappingBtnClicked = useSelector(
		(state: RootState) => state.taxonomy.isRegenerateMappingBtnClicked,
	);

	const TOKEN_CHECK_INTERVAL = 10000; // 10 sec in milliseconds
	const getUserDetails = async () => {
		const response = await getUserInfo();
		if (response?.statusCode === OK && response.responseBody) {
			storageWrapper.setStorageItem(
				'user',
				JSON.stringify(response.responseBody),
			);
			storageWrapper.setStorageItem('loginState', true);
			dispatch(setLoggedInUser(response.responseBody));
			dispatch(setIsAuthenticated(true));
		} else {
			if (response?.statusCode === BAD_REQUEST) {
				dispatch(setErrorMessage(BAD_REQUEST_MSG));
			} else if (
				response?.statusCode === NOT_FOUND ||
				response?.statusCode === FORBIDDEN
			) {
				dispatch(setErrorMessage(USER_NOT_FOUND));
			} else {
				dispatch(setErrorMessage(APP_GENERAL_ERROR_MSG));
			}
			storageWrapper.setStorageItem('user', '{}');
			dispatch(setLoggedInUser(null));
			storageWrapper.setStorageItem('loginState', false);
			storageCleanAndRedirect();
			dispatch(setIsError(true));
			dispatch(setIsAuthenticated(false));
		}
	};
	function delay(ms: number): Promise<void> {
		return new Promise((resolve) => {
			setTimeout(() => resolve(), ms);
		});
	}
	const adTokenRefresh = async () => {
		try {
			setRefreshing(true);
			await delay(1000);
			const response = await refreshTokenAPI();
			if (response?.statusCode === OK && response.responseBody) {
				const token = response.responseBody?.idToken;
				const decodedPayload = jwtDecode(token);
				storageWrapper.setStorageItem('access-token', token);
				storageWrapper.setStorageItem('id-token', token);
				storageWrapper.setStorageItem('loginState', true);
				storageWrapper.setStorageItem('expiry', decodedPayload.exp);
				getUserDetails();
			} else {
				onLogout(deleteAIGeneratedCardPayload, isRegenerateMappingBtnClicked);
			}
		} catch (error) {
			onLogout(deleteAIGeneratedCardPayload, isRegenerateMappingBtnClicked);
		} finally {
			await delay(500);
			setRefreshing(false);
		}
	};

	useEffect(() => {
		const checkTokenExpiry = () => {
			const currentTime = Math.floor(Date.now() / 1000);
			const tokenExpiry = storageWrapper.getStorageItem('expiry');
			const sec25 = 25; // 25 seconds
			const timeDiff = tokenExpiry - currentTime;
			if (timeDiff < sec25 && timeDiff > 0) {
				adTokenRefresh();
			}
			if (timeDiff < 0) {
				onLogout(deleteAIGeneratedCardPayload, isRegenerateMappingBtnClicked);
			}
			if (Number.isNaN(timeDiff)) {
				clearInterval(intervalId);
			}
		};
		const intervalId = setInterval(() => {
			checkTokenExpiry();
		}, TOKEN_CHECK_INTERVAL);
		return () => {
			clearInterval(intervalId);
		};
	}, []);
	const modalStyles = {
		modalStyle: {
			width: '20rem',
			height: '60rem',
			margin: 'auto',
			overflowX: 'unset',
			overflowY: 'unset',
		},
		refreshTokenStyle: {
			textAlign: 'center',
			height: '5rem',
			display: 'flex',
			alignItems: 'center',
			fontSize: '18px',
			justifyContent: 'center',
		} as CSSProperties,
	};
	return (
		// eslint-disable-next-line react/jsx-no-useless-fragment
		<>
			{children}
			{refreshing && (
				<ModalEle
					open={refreshing}
					sxBoxProps={modalStyles.modalStyle}
					dataTestId="refresh-token-modal"
				>
					<Stack>
						<div style={modalStyles.refreshTokenStyle}>
							Please wait... <br />
							Refreshing Auth Token
						</div>
					</Stack>
				</ModalEle>
			)}
		</>
	);
};

export default AuthProvider;
